import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { AuthCard } from "@/pages/Login";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const CONSENTS = [
  ["terms", <>I agree to the <Link to="/policies/terms" target="_blank" className="underline">Terms of Service</Link></>],
  ["privacy", <>I agree to the <Link to="/policies/privacy" target="_blank" className="underline">Privacy Policy</Link></>],
  ["refund", <>I accept the <Link to="/policies/refund" target="_blank" className="underline">Refund & Cancellation Policy</Link></>],
  ["cookie", <>I accept the <Link to="/policies/cookie" target="_blank" className="underline">Cookie Policy</Link></>],
  ["no_guarantee", <>I understand AetherTags does not guarantee rankings, traffic or sales, and I'm responsible for my listings' accuracy</>],
];

export default function Signup() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consents, setConsents] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const allChecked = CONSENTS.every(([k]) => consents[k]);

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!allChecked) return;
    setBusy(true);
    setError(null);
    try {
      await register({ email, password, consents });
      navigate("/app");
    } catch (err) {
      setError({ msg: errMsg(err), inviteOnly: err?.response?.status === 403 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Create your beta account" sub="Free during the beta. Nothing changes in your shop without your approval.">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-1.5">
          <Label htmlFor="su-email">Email</Label>
          <Input id="su-email" type="email" required data-testid="signup-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="su-password">Password</Label>
          <Input id="su-password" type="password" required minLength={8} data-testid="signup-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>
        <fieldset className="space-y-3 rounded-2xl border border-white/[0.07] bg-white/[0.03] p-4">
          <legend className="sr-only">Consent</legend>
          {CONSENTS.map(([k, label]) => (
            <label key={k} className="flex min-h-[44px] items-center gap-3 text-sm leading-snug cursor-pointer">
              <Checkbox
                data-testid={`signup-consent-${k}`}
                checked={!!consents[k]}
                onCheckedChange={(v) => setConsents({ ...consents, [k]: v === true })}
                className="h-5 w-5 shrink-0"
              />
              <span>{label}</span>
            </label>
          ))}
          <p className="text-xs text-muted-foreground">Policies are Beta drafts. We store a consent record with time, IP and browser.</p>
        </fieldset>
        {error && (error.inviteOnly ? (
          <div data-testid="signup-error-message" role="status" className="rounded-2xl border border-[#E9C98B]/35 bg-[#E9C98B]/[0.07] p-4 text-sm leading-relaxed text-[#F3DDAE]">
            <p className="font-medium text-[#F4F1EA]">Founding beta · invite only</p>
            <p className="mt-1.5">{error.msg}</p>
            <Link to="/#waitlist" data-testid="signup-waitlist-link" className="mt-2 inline-flex min-h-[44px] items-center font-medium text-[#F3DDAE] underline underline-offset-4">Go to the waitlist →</Link>
          </div>
        ) : (
          <p data-testid="signup-error-message" className="text-sm text-destructive">{error.msg}</p>
        ))}
        <Button type="submit" disabled={!allChecked || busy} data-testid="signup-submit-button" className="btn-aurora w-full rounded-2xl h-12">
          {busy ? "Creating…" : "Create account"}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          Already have an account? <Link to="/login" data-testid="signup-to-login-link" className="text-primary hover:underline">Log in</Link>
        </p>
      </form>
    </AuthCard>
  );
}
